import {
	type ContrastLevel,
	type GenerateM3ThemeOptions,
	generateM3Theme,
	type MD3ColorScheme,
	type SchemeVariant,
} from "./theme-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreateMd3ThemeOptions {
	/** Seed color in hex format, e.g. `"#6750A4"`. */
	seedColor: string;
	/** Whether to generate a dark scheme. Defaults to `false`. */
	isDark?: boolean;
	/**
	 * Color scheme variant. Defaults to `"expressive"` (MD3 Expressive 2025).
	 * @see SchemeVariant
	 */
	variant?: SchemeVariant;
	/**
	 * Contrast adjustment. Defaults to `0` (standard).
	 * - `0` — Standard
	 * - `0.5` — Medium contrast
	 * - `1` — Maximum contrast (WCAG AAA)
	 */
	contrastLevel?: ContrastLevel;
}

export interface Md3ThemeResult {
	/**
	 * Flat map of CSS custom properties that can be injected into a style block.
	 * Keys use `--md-sys-color-*` naming.
	 *
	 * @example
	 * Object.entries(cssVariables).forEach(([k, v]) => {
	 *   document.documentElement.style.setProperty(k, v);
	 * });
	 */
	cssVariables: Record<string, string>;
	/** Fully-typed color scheme object with camelCase role names. */
	colorScheme: MD3ColorScheme;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * High-level one-call API to generate a Material 3 Expressive theme.
 *
 * Returns both a CSS variable map (for injection into the DOM or SSR style tags)
 * and a typed `MD3ColorScheme` object for use in TypeScript.
 *
 * Uses **SpecVersion 2025** and **Variant.EXPRESSIVE** by default, aligning with
 * the MD3 Expressive 2025 color specification.
 *
 * @example
 * const { cssVariables } = createMd3ExpressiveTheme({ seedColor: "#6750A4" });
 * // Inject at runtime:
 * for (const [k, v] of Object.entries(cssVariables)) {
 *   document.documentElement.style.setProperty(k, v);
 * }
 *
 * @example
 * // High-contrast dark scheme with tonal_spot variant
 * const theme = createMd3ExpressiveTheme({
 *   seedColor: "#00639B",
 *   isDark: true,
 *   variant: "tonal_spot",
 *   contrastLevel: 1,
 * });
 */
export function createMd3ExpressiveTheme(
	options: CreateMd3ThemeOptions,
): Md3ThemeResult {
	const { seedColor, isDark = false, variant, contrastLevel } = options;

	const themeOptions: GenerateM3ThemeOptions = { variant, contrastLevel };
	const colorScheme = generateM3Theme(
		seedColor,
		isDark ? "dark" : "light",
		themeOptions,
	);

	const cssVariables = schemeToCssVariables(colorScheme);

	return { cssVariables, colorScheme };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a camelCase color scheme to `--md-sys-color-*` CSS variable map. */
function schemeToCssVariables(scheme: MD3ColorScheme): Record<string, string> {
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(scheme)) {
		const kebabKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
		result[`--md-sys-color-${kebabKey}`] = value;
	}
	return result;
}
